// useStandingAgentLive — keeps a Pulsars page in step with the server without
// ever patching from a push (standing-agent-ui "Live updates", design D10).
//
//   * reseed() is single-flight with ONE trailing pass: a nudge that lands while
//     a fetch is in flight triggers exactly one more fetch, never zero, never N.
//   * It runs on mount and on every agent-hub (re)connect (`connectionEpoch`), so
//     anything missed while disconnected is recovered by re-querying.
//   * `standing.status` / `standing.batch` events (and run events, which move a
//     check-in along) only mean "refetch"; `agentId` narrows that to one pulsar.
//   * A 30-second poll backs up the push, and pauses while the browser tab is
//     hidden — it fires once when the tab becomes visible again.

import { useCallback, useEffect, useRef } from 'react';
import type { AgentActivityEvent } from '@shared/api-types';
import { useAgentActivity } from '../../lib/agentActivity';

/** Polling fallback interval. */
export const LIVE_POLL_MS = 30_000;

export interface StandingLiveOptions {
  /** Only react to standing events for this pulsar (other pulsars' nudges are ignored). */
  agentId?: string;
  /** Override the fallback interval (tests). */
  intervalMs?: number;
  /** Which hub events mean "refetch" (default: standing nudges + run status). The AI Playground passes its own. */
  isRelevant?: (e: AgentActivityEvent) => boolean;
}

/** True when an agent-hub event should make a Pulsars page refetch. */
export function isRelevantEvent(e: AgentActivityEvent, agentId?: string): boolean {
  if (e.kind === 'standing.status' || e.kind === 'standing.batch') {
    return agentId == null || e.standingAgentId == null || e.standingAgentId === agentId;
  }
  // Run events: a check-in or approved actions progressing.
  return e.kind === 'status';
}

/**
 * Wire `load` to the live triggers. Returns `reseed` for callers that want to
 * refetch after their own mutation.
 */
export function useStandingAgentLive(load: () => Promise<void>, options: StandingLiveOptions = {}) {
  const agent = useAgentActivity();
  const { agentId, intervalMs = LIVE_POLL_MS, isRelevant } = options;
  const relevantRef = useRef(isRelevant);
  useEffect(() => {
    relevantRef.current = isRelevant;
  }, [isRelevant]);
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  const inFlight = useRef(false);
  const pending = useRef(false);
  const reseed = useCallback(async () => {
    if (inFlight.current) {
      pending.current = true;
      return;
    }
    inFlight.current = true;
    try {
      do {
        pending.current = false;
        await loadRef.current();
      } while (pending.current);
    } finally {
      inFlight.current = false;
    }
  }, []);

  // Mount + every (re)connect.
  const epoch = agent?.connectionEpoch ?? 0;
  useEffect(() => {
    void reseed();
  }, [epoch, reseed]);

  // Push nudges.
  useEffect(() => {
    if (!agent) return undefined;
    return agent.onAgentActivity((e) => {
      if (relevantRef.current ? relevantRef.current(e) : isRelevantEvent(e, agentId)) void reseed();
    });
  }, [agent, agentId, reseed]);

  // Polling fallback, paused while hidden; refresh when the tab comes back.
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') void reseed();
    };
    const id = window.setInterval(tick, intervalMs);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void reseed();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [intervalMs, reseed]);

  return reseed;
}
